import { useRef, useLayoutEffect } from 'react';
import { motion } from 'framer-motion';
import IconBack from '../UISystem/Icons/IconBack';
import useSwipeBack from '../../hooks/useSwipeBack';

/**
 * ConnectionDetailShell — shared chrome for the three connection detail pages
 * (Profile, Anon, Manual). Owns the swipe-back gesture, white card, top edge
 * fade, back-button gradient, and the scroll container (with scroll-to-top on
 * mount). The back button and a completed swipe both fire onBack.
 *
 * Page-level overlays (lightbox, GroupSelector, LocationOverlay) are all
 * position:fixed, so each page renders them as siblings of this shell.
 *
 * Props:
 *   onBack - back handler; fired by the back button and a completed swipe
 *   swipeEnabled - false disables the swipe (e.g. while an overlay is open)
 *   topRight - optional action in the mirrored top-right corner. The corner
 *     grammar from nav-profile-top: top-left navigates, top-right acts. The
 *     page decides what belongs there (app chat on a live THREAD connection,
 *     save-contact on the sans-chat variants) and renders nothing when the
 *     action doesn't apply.
 *   children - scroll container content
 */
const ConnectionDetailShell = ({ onBack, swipeEnabled = true, topRight, children }) => {
  const scrollContainerRef = useRef(null);
  const { dragProps, swipeStyle, backdropElement } = useSwipeBack(onBack, swipeEnabled);

  // Reset scroll position when the page mounts (before paint)
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, []);

  return (
    <>
      {backdropElement}
      <motion.div
        {...dragProps}
        style={{
          ...swipeStyle,
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: '100dvh',
          backgroundColor: 'transparent',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '0 16px 8px',
        }}
      >
        {/* White card container */}
        <div
          style={{
            position: 'relative',
            backgroundColor: '#FFFFFF',
            borderRadius: '32px',
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Thin top edge fade - subtle line across top */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '5px',
              background: 'linear-gradient(to bottom, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)',
              pointerEvents: 'none',
              zIndex: 5,
              borderTopLeftRadius: '32px',
              borderTopRightRadius: '32px',
            }}
          />

          {/* Circular gradient around back button */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100px',
              height: '100px',
              background:
                'radial-gradient(circle at 28px 27px, #FFFFFF 0%, #FFFFFF 40%, rgba(255, 255, 255, 0) 70%)',
              pointerEvents: 'none',
              zIndex: 5,
              borderTopLeftRadius: '32px',
            }}
          />

          {/* Circular gradient around the top-right action — mirrors the back
              button's, so the corner action sits on the same legible ground */}
          {topRight && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '100px',
                height: '100px',
                background:
                  'radial-gradient(circle at 72px 27px, #FFFFFF 0%, #FFFFFF 40%, rgba(255, 255, 255, 0) 70%)',
                pointerEvents: 'none',
                zIndex: 5,
                borderTopRightRadius: '32px',
              }}
            />
          )}

          {/* Back button */}
          <div style={{ position: 'absolute', top: '5px', left: '6px', zIndex: 10 }}>
            <IconBack onClick={onBack} />
          </div>

          {/* Top-right action */}
          {topRight && (
            <div style={{ position: 'absolute', top: '9px', right: '10px', zIndex: 10 }}>
              {topRight}
            </div>
          )}

          {/* Scrollable content area */}
          <div
            ref={scrollContainerRef}
            style={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              WebkitOverflowScrolling: 'touch',
              paddingBottom: '16px',
            }}
          >
            {children}
          </div>
        </div>
      </motion.div>
    </>
  );
};

export default ConnectionDetailShell;
